import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Tooltip, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { BACKEND_URL } from "../../lib/backend";
import { fmtRange, initials } from "../../lib/events";

const TILE_OSM = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const ATTRIB = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

/** Grid size in degrees per zoom level — coarse when zoomed out, per-city when zoomed in. */
function cellSize(zoom) {
  if (zoom <= 2) return 12;
  if (zoom <= 3) return 7;
  if (zoom <= 4) return 4;
  if (zoom <= 5) return 2;
  if (zoom <= 7) return 0.6;
  return 0.05;
}

function clusterize(events, zoom) {
  const size = cellSize(zoom);
  const cells = new Map();
  for (const e of events) {
    if (e.lat == null || e.lng == null) continue;
    const key = `${Math.floor(e.lat / size)}:${Math.floor(e.lng / size)}`;
    if (!cells.has(key)) cells.set(key, { events: [], lat: 0, lng: 0 });
    const c = cells.get(key);
    c.events.push(e);
    c.lat += e.lat; c.lng += e.lng;
  }
  return [...cells.values()].map((c) => ({ ...c, lat: c.lat / c.events.length, lng: c.lng / c.events.length }));
}

function iconFor(cluster, dm) {
  const n = cluster.events.length;
  const first = cluster.events[0];
  const bg = dm ? "#0f1718" : "#ffffff";
  const fg = dm ? "#67e8f9" : "#006874";
  const border = dm ? "#334155" : "#e7e8e9";
  const logo = n === 1 && first.logo_url
    ? `<img src="${BACKEND_URL}${first.logo_url}" alt="" style="width:100%;height:100%;object-fit:contain;padding:5px" onerror="this.style.display='none';this.nextSibling.style.display='flex'"/><span style="display:none;align-items:center;justify-content:center;width:100%;height:100%;font-weight:700;font-size:11px;color:${fg}">${initials(first.name)}</span>`
    : `<span style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;font-weight:700;font-size:${n === 1 ? 11 : 13}px;color:${fg}">${n === 1 ? initials(first.name) : n}</span>`;
  const size = n === 1 ? 38 : Math.min(56, 38 + Math.log2(n) * 6);
  return L.divIcon({
    className: "ti-event-marker",
    html: `<div style="width:${size}px;height:${size}px;border-radius:${n === 1 ? "12px" : "50%"};background:${bg};border:2px solid ${n > 1 ? fg : border};box-shadow:0 2px 10px rgba(0,0,0,.18);overflow:hidden;display:flex">${logo}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function Markers({ events, dm, onSelect }) {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  useMapEvents({ zoomend: () => setZoom(map.getZoom()) });
  const clusters = useMemo(() => clusterize(events, zoom), [events, zoom]);
  return clusters.map((c, i) => (
    <Marker
      key={`${zoom}-${i}`}
      position={[c.lat, c.lng]}
      icon={iconFor(c, dm)}
      eventHandlers={{
        click: () => {
          if (c.events.length === 1) onSelect(c.events[0]);
          else map.setView([c.lat, c.lng], Math.min(map.getZoom() + 2, 12));
        },
      }}
    >
      <Tooltip direction="top" offset={[0, -18]} opacity={1}>
        <div style={{ fontSize: 12, maxWidth: 240 }}>
          {c.events.slice(0, 6).map((e) => (
            <div key={e.id} style={{ marginBottom: 2 }}><strong>{e.name}</strong><br /><span style={{ opacity: 0.7 }}>{fmtRange(e.start_date, e.end_date)} · {e.city}</span></div>
          ))}
          {c.events.length > 6 && <div style={{ opacity: 0.7 }}>+{c.events.length - 6} more — click to zoom</div>}
        </div>
      </Tooltip>
    </Marker>
  ));
}

export function EventMap({ events, dm }) {
  const navigate = useNavigate();
  const located = events.filter((e) => e.lat != null && e.lng != null);
  const missing = events.length - located.length;
  return (
    <div data-testid="events-map" className={`rounded-2xl border overflow-hidden ${dm ? "border-slate-700/50" : "border-[#e7e8e9]"}`}>
      <MapContainer center={[30, 10]} zoom={2} minZoom={2} maxZoom={13} scrollWheelZoom={false} className={dm ? "ti-map-dark" : ""} style={{ height: 520, width: "100%", background: dm ? "#0f1718" : "#eef2f3" }} worldCopyJump>
        <TileLayer url={TILE_OSM} attribution={ATTRIB} subdomains="abc" />
        <Markers events={located} dm={dm} onSelect={(e) => navigate(`/events/${e.slug}`)} />
      </MapContainer>
      <style>{`.ti-map-dark .leaflet-tile-pane{filter:invert(1) hue-rotate(180deg) brightness(.85) contrast(.9) saturate(.6)} .ti-event-marker{background:none;border:0} .leaflet-container a.leaflet-attribution-flag{display:none!important}`}</style>
      <p className={`px-4 py-2 text-[11px] ${dm ? "bg-[#1a2324] text-slate-500" : "bg-white text-[#6b7a7d]"}`} data-testid="events-map-footnote">
        {located.length} event{located.length !== 1 ? "s" : ""} on the map · click a numbered circle to zoom, a logo to open the event page
        {missing > 0 ? ` · ${missing} event${missing !== 1 ? "s" : ""} without a confirmed venue city are listed only` : ""}
      </p>
    </div>
  );
}

export default EventMap;
